import { ElementType } from "react";
import {
    ArrowsRightLeftIcon,
    BriefcaseIcon,
    BuildingLibraryIcon,
    BuildingOffice2Icon,
    GlobeAltIcon,
    UsersIcon,
} from "@heroicons/react/24/solid";
import type { ComboBoxMultipleOption } from "@/common/ComboBoxMultiple";
import { CompanyFit } from "@/types/generated/CompanyFit.ts";
import { IcpCriterion } from "@/types/generated/IcpCriterion.ts";
import { IcpDisqualifier } from "@/types/generated/IcpDisqualifier.ts";
import { IcpField } from "@/types/generated/IcpField.ts";
import { IcpProfile } from "@/types/generated/IcpProfile.ts";
import {
    IcpVocabularyResponse,
    Workspace,
} from "@/types/generated/management-api-types";
import { chartColor } from "@/common/ui/chartColors.ts";

/** The fit tiers, in rank order (best first), as data-api spells them. */
export const FIT_TIERS = ["strong", "fair", "poor", "unknown"] as const;
export type FitTier = (typeof FIT_TIERS)[number];

export const FIT_TIER_LABELS: Record<FitTier, string> = {
    strong: "Strong",
    fair: "Fair",
    poor: "Poor",
    unknown: "Unknown",
};

/** Badge colours per tier; Unknown is neutral because it is "no data", not a verdict. */
export const FIT_TIER_COLORS: Record<
    FitTier,
    "green" | "amber" | "red" | "zinc"
> = {
    strong: "green",
    fair: "amber",
    poor: "red",
    unknown: "zinc",
};

/** The same colours as a filled bar: a tier's share, or a company's score. */
export const FIT_TIER_BAR_COLORS: Record<FitTier, string> = {
    strong: "bg-chart-good",
    fair: "bg-chart-warning",
    poor: "bg-chart-critical",
    unknown: "bg-chart-faint",
};

/** The same colours as a chart series per tier. */
export const FIT_TIER_CHART_COLORS: Record<FitTier, string> = {
    strong: chartColor.good,
    fair: chartColor.warning,
    poor: chartColor.critical,
    unknown: chartColor.faint,
};

/** The company facts a profile scores on: every field but the questions. */
export type FactField = Exclude<IcpField, "question">;

export type Fact = {
    field: FactField;
    label: string;
    /** Where the values come from, in a few words. */
    hint: string;
    /** The placeholder of an empty picker: what choosing nothing means. */
    any: string;
    icon: ElementType;
    /** Read per company by Spectacle, which needs Lens (plan.md D3). */
    needsLens: boolean;
    /** Whole-company facts can rule a company out; location only scores. */
    canRuleOut: boolean;
};

/**
 * The facts, in the order the editor lists them: the two that usually carry
 * the profile first, then location, then the three Spectacle reads per
 * company.
 */
export const FACTS: Fact[] = [
    {
        field: "industry",
        label: "Industry",
        hint: "Software, retail, consulting…",
        any: "Any industry",
        icon: BuildingOffice2Icon,
        needsLens: false,
        canRuleOut: false,
    },
    {
        field: "employees",
        label: "Company size",
        hint: "By number of employees",
        any: "Any size",
        icon: UsersIcon,
        needsLens: false,
        canRuleOut: true,
    },
    {
        field: "country",
        label: "Country",
        hint: "Where the company is based",
        any: "Anywhere",
        icon: GlobeAltIcon,
        needsLens: false,
        canRuleOut: false,
    },
    {
        field: "organizationType",
        label: "Organization type",
        hint: "Private, public, non-profit, school…",
        any: "Any type",
        icon: BuildingLibraryIcon,
        needsLens: true,
        canRuleOut: true,
    },
    {
        field: "businessModel",
        label: "Business model",
        hint: "Software, services, goods, agency…",
        any: "Any model",
        icon: BriefcaseIcon,
        needsLens: true,
        canRuleOut: true,
    },
    {
        field: "b2bB2c",
        label: "Their customers",
        hint: "Who these companies sell to, not who you sell to",
        any: "Anyone",
        icon: ArrowsRightLeftIcon,
        needsLens: true,
        canRuleOut: true,
    },
];

export function factOf(field: FactField): Fact {
    return FACTS.find((fact) => fact.field === field)!;
}

export const ICP_FIELD_LABELS: Record<IcpField, string> = {
    ...(Object.fromEntries(FACTS.map((f) => [f.field, f.label])) as Record<
        FactField,
        string
    >),
    question: "Question",
};

/** The values a fact can take, as picker options; only these reach a rule. */
export function optionsFor(
    field: FactField,
    vocabulary: IcpVocabularyResponse | undefined,
): ComboBoxMultipleOption[] {
    if (!vocabulary) return [];
    switch (field) {
        case "industry":
            return vocabulary.industries.map((i) => ({
                id: i.label,
                label: i.label,
                description: i.group,
            }));
        case "employees":
            return vocabulary.employees.map(asOption);
        case "country":
            return vocabulary.countries.map(asOption);
        case "organizationType":
            return vocabulary.organizationTypes.map(asOption);
        case "businessModel":
            return vocabulary.businessModels.map(asOption);
        case "b2bB2c":
            return vocabulary.audiences.map(asOption);
    }
}

function asOption(option: {
    value: string;
    label: string;
}): ComboBoxMultipleOption {
    return { id: option.value, label: option.label };
}

/** A company's own values as the editor names them: "51-200 employees", not the stored code. */
export function valueLabels(
    field: IcpField,
    values: string[],
    vocabulary: IcpVocabularyResponse | undefined,
): string {
    if (field === "question") {
        return values.map((value) => (value === "no" ? "No" : "Yes")).join("");
    }
    const options = optionsFor(field, vocabulary);
    return values
        .map(
            (value) =>
                options.find((option) => option.id === value)?.label ?? value,
        )
        .join(", ");
}

/**
 * How much a fact or question counts. Only the ratio between rules reaches
 * the score (points earned ÷ points possible), so three steps say everything
 * a number would, and a drafted 35 reads as High without being rewritten.
 */
export const WEIGHTS = { low: 10, medium: 20, high: 40 } as const;
export type Weight = keyof typeof WEIGHTS;
export const WEIGHT_ORDER: Weight[] = ["low", "medium", "high"];
export const WEIGHT_LABELS: Record<Weight, string> = {
    low: "Low",
    medium: "Medium",
    high: "High",
};

export function weightOf(points: number): Weight {
    if (points >= 30) return "high";
    if (points >= 15) return "medium";
    return "low";
}

export const MAX_CRITERIA = 11;
export const MAX_QUESTIONS = 5;

/**
 * The workspace payload carries the rules as a free object (the OpenApi
 * document cannot name data-api's type); this is the one cast.
 */
export function icpProfileOf(workspace: Workspace): IcpProfile | null {
    return (workspace.icpProfile as IcpProfile | null | undefined) ?? null;
}

export function emptyProfile(): IcpProfile {
    return { criteria: [], disqualifiers: [], questions: [] };
}

/** Points a company earns when every criterion matches: the score's denominator. */
export function maxPoints(profile: IcpProfile): number {
    return profile.criteria.reduce((sum, c) => sum + c.points, 0);
}

/** An id the Api accepts (letters, digits, dashes, underscores), unique within the profile. */
export function newRuleId(prefix: string, taken: Set<string>): string {
    let n = 1;
    while (taken.has(`${prefix}-${n}`)) {
        n += 1;
    }
    return `${prefix}-${n}`;
}

function takenIds(profile: IcpProfile): Set<string> {
    const ids = new Set<string>();
    for (const c of profile.criteria) ids.add(c.id);
    for (const d of profile.disqualifiers) ids.add(d.id);
    for (const q of profile.questions) ids.add(q.id);
    return ids;
}

/*
 * The editor shows one row per fact, so a fact's criterion and its rule-out
 * are looked up by field. The wire format allows several criteria on one
 * field; the editor reads the first and, on an edit, replaces them all.
 */

export function criterionFor(
    profile: IcpProfile,
    field: FactField,
): IcpCriterion | undefined {
    return profile.criteria.find((c) => c.field === field);
}

export function ruleOutFor(
    profile: IcpProfile,
    field: FactField,
): IcpDisqualifier | undefined {
    return profile.disqualifiers.find((d) => d.field === field);
}

/** The profile with this fact's ideal values and weight; no values, no rule. */
export function withFact(
    profile: IcpProfile,
    field: FactField,
    values: string[],
    points: number,
): IcpProfile {
    const existing = criterionFor(profile, field);
    const others = profile.criteria.filter((c) => c.field !== field);
    if (values.length === 0) {
        return { ...profile, criteria: others };
    }
    const criterion: IcpCriterion = {
        id: existing?.id ?? newRuleId(field, takenIds(profile)),
        label: factOf(field).label,
        field,
        questionId: null,
        values,
        points,
    };
    return { ...profile, criteria: [...others, criterion] };
}

/** The profile ruling out companies with one of these values; none, and the rule-out is gone. */
export function withRuleOut(
    profile: IcpProfile,
    field: FactField,
    values: string[],
): IcpProfile {
    const existing = ruleOutFor(profile, field);
    const others = profile.disqualifiers.filter((d) => d.field !== field);
    if (values.length === 0) {
        return { ...profile, disqualifiers: others };
    }
    const disqualifier: IcpDisqualifier = {
        id: existing?.id ?? newRuleId(`not-${field}`, takenIds(profile)),
        label: `Not ${factOf(field).label.toLowerCase()}`,
        field,
        questionId: null,
        values,
    };
    return { ...profile, disqualifiers: [...others, disqualifier] };
}

/**
 * A question is a question plus the criterion that scores its answer. The
 * editor keeps them as one row.
 */
export type QuestionRule = {
    id: string;
    text: string;
    answer: "yes" | "no";
    points: number;
};

export function questionRules(profile: IcpProfile): QuestionRule[] {
    return profile.questions.map((question) => {
        const criterion = profile.criteria.find(
            (c) => c.field === "question" && c.questionId === question.id,
        );
        return {
            id: question.id,
            text: question.text,
            answer: criterion?.values[0] === "no" ? "no" : "yes",
            points: criterion?.points ?? WEIGHTS.medium,
        };
    });
}

export function withQuestion(
    profile: IcpProfile,
    rule: QuestionRule,
): IcpProfile {
    const exists = profile.questions.some((q) => q.id === rule.id);
    const criterion: IcpCriterion = {
        id: `${rule.id}-answer`,
        label: rule.text,
        field: "question",
        questionId: rule.id,
        values: [rule.answer],
        points: rule.points,
    };
    return {
        ...profile,
        questions: exists
            ? profile.questions.map((q) =>
                  q.id === rule.id ? { ...q, text: rule.text } : q,
              )
            : [
                  ...profile.questions,
                  { id: rule.id, version: 1, text: rule.text },
              ],
        criteria: exists
            ? profile.criteria.map((c) =>
                  c.field === "question" && c.questionId === rule.id
                      ? criterion
                      : c,
              )
            : [...profile.criteria, criterion],
    };
}

export function withoutQuestion(profile: IcpProfile, id: string): IcpProfile {
    return {
        ...profile,
        questions: profile.questions.filter((q) => q.id !== id),
        criteria: profile.criteria.filter(
            (c) => !(c.field === "question" && c.questionId === id),
        ),
    };
}

export function newQuestionId(profile: IcpProfile): string {
    return newRuleId("q", takenIds(profile));
}

/**
 * The share of the profile's points a company needs data for before it gets
 * a tier; below it the company is Unknown. Mirrors `MIN_COVERAGE_SHARE` in
 * data-api (`icp_fit/model.rs`).
 */
export const MIN_COVERAGE_SHARE = 0.6;

/** How a company did on one rule of the profile. */
export type FitOutcome = "matches" | "misses" | "noData";

/** One rule of the profile as it went for one company. */
export type FitReason = {
    id: string;
    field: IcpField;
    /** The fact's name, or the question as asked. */
    label: string;
    /**
     * What the company has for the fact, as stored; "yes" or "no" for a
     * question. Empty without data.
     */
    values: string[];
    outcome: FitOutcome;
    points: number;
};

export type FitExplanation = {
    /** Every criterion, in the profile's order. */
    reasons: FitReason[];
    /** The rule-outs that fired; one is enough for a Poor fit. */
    ruledOutBy: Omit<FitReason, "outcome" | "points">[];
    /** The share of the profile's points the company has data for, 0 to 1. */
    coverage: number;
};

/** What the company has for a fact; empty when the score had nothing to go on. */
function factValues(field: FactField, fit: CompanyFit): string[] {
    if (field === "country") return fit.countries;
    const value = {
        industry: fit.industry,
        employees: fit.employees,
        organizationType: fit.organizationType,
        businessModel: fit.businessModel,
        b2bB2c: fit.b2bB2c,
    }[field];
    return value === null ? [] : [value];
}

/**
 * The company's side of a rule. A company in several countries shows the
 * ones the rule names when it matches, since those earned the points.
 */
function ruleValues(
    rule: IcpCriterion | IcpDisqualifier,
    fit: CompanyFit,
    outcome: FitOutcome,
): string[] {
    if (outcome === "noData") return [];
    if (rule.field === "question") {
        const wanted = rule.values[0] === "no" ? "no" : "yes";
        if (outcome === "matches") return [wanted];
        return [wanted === "yes" ? "no" : "yes"];
    }
    const values = factValues(rule.field, fit);
    return outcome === "matches"
        ? values.filter((value) => rule.values.includes(value))
        : values;
}

function ruleLabel(rule: IcpCriterion | IcpDisqualifier): string {
    return rule.field === "question"
        ? rule.label
        : ICP_FIELD_LABELS[rule.field];
}

/**
 * Why a company has the fit it has: each criterion with the company's own
 * value beside it, and the rule-outs that fired.
 */
export function explainFit(
    profile: IcpProfile,
    fit: CompanyFit,
): FitExplanation {
    const reasons = profile.criteria.map((criterion): FitReason => {
        const outcome: FitOutcome = fit.missing.includes(criterion.id)
            ? "noData"
            : fit.matched.includes(criterion.id)
              ? "matches"
              : "misses";
        return {
            id: criterion.id,
            field: criterion.field,
            label: ruleLabel(criterion),
            values: ruleValues(criterion, fit, outcome),
            outcome,
            points: criterion.points,
        };
    });
    const ruledOutBy = profile.disqualifiers
        .filter((disqualifier) => fit.matched.includes(disqualifier.id))
        .map((disqualifier) => ({
            id: disqualifier.id,
            field: disqualifier.field,
            label: ruleLabel(disqualifier),
            values: ruleValues(disqualifier, fit, "matches"),
        }));
    const known = reasons
        .filter((reason) => reason.outcome !== "noData")
        .reduce((sum, reason) => sum + reason.points, 0);
    const max = maxPoints(profile);
    return { reasons, ruledOutBy, coverage: max === 0 ? 0 : known / max };
}
